import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getWithdrawals, addWithdrawal, deleteWithdrawal, getManagers } from '../api';
import {
  formatNumber,
  parseFormattedNumber,
  convertDateForApi,
  formatDateForDisplay,
  getTodayByLocale,
  getMonthStartByLocale,
  getDateConfig,
  switchDateLocale,
  usePagination,
} from '../utils';
import DatePicker from 'react-multi-date-picker';

function Withdrawals({ dateLocale }) {
  const dateConfig = getDateConfig(dateLocale);

  const [withdrawals, setWithdrawals] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    manager_id: '',
    amount: '',
    exchange_rate: '',
    currency: 'IRR',
    withdrawal_date: getTodayByLocale(dateLocale),
    description: '',
    details: '',
  });
  const [filters, setFilters] = useState({
    manager_id: '',
    currency: '',
    startDate: getMonthStartByLocale(dateLocale),
    endDate: getTodayByLocale(dateLocale),
  });
  const prevLocaleRef = useRef(dateLocale);
  const withdrawalsPagination = usePagination(withdrawals);
  const goToWithdrawalsPage = withdrawalsPagination.goToPage;

  const calculateUsdEquivalent = useCallback((withdrawal) => {
    if (!withdrawal || typeof withdrawal !== 'object') {
      return null;
    }

    const hasAmount = withdrawal.amount !== null && withdrawal.amount !== undefined;
    const amountValue = hasAmount ? parseFloat(withdrawal.amount) : null;
    const exchangeRateValue =
      withdrawal.exchange_rate !== null && withdrawal.exchange_rate !== undefined
        ? parseFloat(withdrawal.exchange_rate)
        : null;

    const isValidAmount = amountValue !== null && !Number.isNaN(amountValue);
    const isValidRate =
      exchangeRateValue !== null && !Number.isNaN(exchangeRateValue) && exchangeRateValue > 0;

    const usdEquivalentRaw =
      withdrawal.currency === 'IRR' && isValidAmount && isValidRate
        ? amountValue / exchangeRateValue
        : withdrawal.currency === 'USD' && isValidAmount
        ? amountValue
        : null;

    if (usdEquivalentRaw === null || !isFinite(usdEquivalentRaw)) {
      return null;
    }

    return parseFloat(usdEquivalentRaw.toFixed(2));
  }, []);

  const { totalAmount, totalUsdEquivalent } = useMemo(() => {
    if (
      !Array.isArray(withdrawalsPagination.paginatedItems) ||
      withdrawalsPagination.paginatedItems.length === 0
    ) {
      return { totalAmount: 0, totalUsdEquivalent: 0 };
    }

    let amountSum = 0;
    let usdSum = 0;

    withdrawalsPagination.paginatedItems.forEach((withdrawal) => {
      const hasAmount = withdrawal.amount !== null && withdrawal.amount !== undefined;
      const amountValue = hasAmount ? parseFloat(withdrawal.amount) : null;
      if (amountValue !== null && !Number.isNaN(amountValue)) {
        amountSum += amountValue;
      }

      const usdValue = calculateUsdEquivalent(withdrawal);
      if (usdValue !== null && !Number.isNaN(usdValue)) {
        usdSum += usdValue;
      }
    });

    return {
      totalAmount: amountSum,
      totalUsdEquivalent: parseFloat(usdSum.toFixed(2)),
    };
  }, [withdrawalsPagination.paginatedItems, calculateUsdEquivalent]);

  const renderPaginationControls = (pagination) => {
    if (pagination.totalItems === 0) {
      return null;
    }

    return (
      <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between border-top px-3 py-3 gap-3">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small">تعداد در صفحه</span>
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto' }}
            value={pagination.pageSize}
            onChange={(e) => pagination.setPageSize(Number(e.target.value))}
          >
            {pagination.pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span className="text-muted small">
            نمایش {pagination.rangeStart} تا {pagination.rangeEnd} از {pagination.totalItems}
          </span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={pagination.prevPage}
            disabled={!pagination.canGoPrev}
          >
            قبلی
          </button>
          <span className="text-muted small">
            صفحه {pagination.currentPage} از {pagination.totalPages}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={pagination.nextPage}
            disabled={!pagination.canGoNext}
          >
            بعدی
          </button>
        </div>
      </div>
    );
  };
  useEffect(() => {
    const prevLocale = prevLocaleRef.current;
    if (prevLocale !== dateLocale) {
      setFormData((prev) => ({
        ...prev,
        withdrawal_date: switchDateLocale(prev.withdrawal_date, prevLocale, dateLocale),
      }));
      setFilters((prev) => ({
        ...prev,
        startDate:
          prev.startDate
            ? switchDateLocale(prev.startDate, prevLocale, dateLocale)
            : getMonthStartByLocale(dateLocale),
        endDate:
          prev.endDate
            ? switchDateLocale(prev.endDate, prevLocale, dateLocale)
            : getTodayByLocale(dateLocale),
      }));
      prevLocaleRef.current = dateLocale;
    }
  }, [dateLocale]);

  const loadData = useCallback(
    async (currentFilters = filters) => {
    try {
      setLoading(true);
      const params = {};
      if (currentFilters.manager_id) {
        params.manager_id = currentFilters.manager_id;
      }
      if (currentFilters.currency) {
        params.currency = currentFilters.currency;
      }
      const startDateValue = convertDateForApi(currentFilters.startDate, dateLocale);
      if (startDateValue) {
        params.start_date = startDateValue;
      }
      const endDateValue = convertDateForApi(currentFilters.endDate, dateLocale);
      if (endDateValue) {
        params.end_date = endDateValue;
      }
      const [withdrawalsRes, managersRes] = await Promise.all([getWithdrawals(params), getManagers()]);
      setWithdrawals(withdrawalsRes.data);
      setManagers(managersRes.data);
      setError('');
    } catch (err) {
      setError('خطا در بارگذاری اطلاعات');
    } finally {
      setLoading(false);
    }
    },
    [filters, dateLocale]
  );

  useEffect(() => {
    loadData(filters);
  }, [filters, loadData]);

  useEffect(() => {
    goToWithdrawalsPage(1);
  }, [withdrawals, goToWithdrawalsPage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.manager_id || !formData.amount || !formData.withdrawal_date) {
      setError('مدیر، مبلغ و تاریخ الزامی است');
      return;
    }

    const exchangeRateRaw = parseFormattedNumber(formData.exchange_rate);
    const exchangeRateValue =
      exchangeRateRaw === '' ? null : parseFloat(exchangeRateRaw);

    if (formData.currency === 'IRR') {
      if (!exchangeRateValue || Number.isNaN(exchangeRateValue) || exchangeRateValue <= 0) {
        setError('نرخ تبدیل برای برداشت ریالی الزامی است');
        return;
      }
    }

    try {
      const gregorianDate = convertDateForApi(formData.withdrawal_date, dateLocale);
      await addWithdrawal({
        manager_id: formData.manager_id,
        amount: parseFloat(parseFormattedNumber(formData.amount)),
        currency: formData.currency,
        exchange_rate:
          formData.currency === 'IRR' && exchangeRateValue && !Number.isNaN(exchangeRateValue)
            ? exchangeRateValue
            : null,
        withdrawal_date: gregorianDate,
        description: formData.description,
        details: formData.details,
      });
      setSuccess('برداشت با موفقیت ثبت شد');
      setError('');
      setFormData({
        manager_id: '',
        amount: '',
        exchange_rate: '',
        currency: 'IRR',
        withdrawal_date: getTodayByLocale(dateLocale),
        description: '',
        details: '',
      });
      loadData(filters);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('خطا در ثبت برداشت');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این برداشت مطمئن هستید؟')) return;

    try {
      await deleteWithdrawal(id);
      setSuccess('برداشت حذف شد');
      loadData(filters);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('خطا در حذف برداشت');
    }
  };

  const resetFilters = () => {
    setFilters({
      manager_id: '',
      currency: '',
      startDate: getMonthStartByLocale(dateLocale),
      endDate: getTodayByLocale(dateLocale),
    });
  };

  if (loading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center w-100"
        style={{ minHeight: '50vh' }}
      >
        <div className="spinner-border text-primary me-3" role="status" />
        <span className="fw-bold text-muted">در حال بارگذاری...</span>
      </div>
    );
  }

  return (
    <div className="d-grid gap-4">
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setError('')}
          />
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setSuccess('')}
          />
        </div>
      )}

      <div className="row g-4">
        <div className="col-12 col-xl-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-transparent border-bottom-0 pb-0">
              <h2 className="h5 mb-1">ثبت برداشت جدید</h2>
              <p className="text-muted small mb-0">جزئیات برداشت را وارد کنید</p>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit} className="row g-3">
                <div className="col-12">
                  <label className="form-label">مدیر</label>
                  <select
                    className="form-select"
                    value={formData.manager_id}
                    onChange={(e) => setFormData({ ...formData, manager_id: e.target.value })}
                  >
                    <option value="">انتخاب مدیر</option>
                    {managers.map((manager) => (
                      <option key={manager.id} value={manager.id}>
                        {manager.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-sm-6">
                  <label className="form-label">نوع ارز</label>
                  <select
                    className="form-select"
                    value={formData.currency}
                    onChange={(e) =>
                      setFormData((prev) => {
                        const nextCurrency = e.target.value;
                        return {
                          ...prev,
                          currency: nextCurrency,
                          exchange_rate: nextCurrency === 'IRR' ? prev.exchange_rate : '',
                        };
                      })
                    }
                  >
                    <option value="IRR">ریالی (تومان)</option>
                    <option value="USD">دلاری (USD)</option>
                  </select>
                </div>
                <div className="col-12 col-sm-6">
                  <label className="form-label">مبلغ</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      value={formData.amount}
                      onChange={(e) => {
                        const rawValue = parseFormattedNumber(e.target.value);
                        if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
                          setFormData({ ...formData, amount: formatNumber(rawValue) });
                        }
                      }}
                      placeholder={formData.currency === 'USD' ? 'مثلاً 150' : 'مثلاً 2500000'}
                    />
                    <span className="input-group-text">
                      {formData.currency === 'USD' ? 'دلار' : 'تومان'}
                    </span>
                  </div>
                </div>
                <div className="col-12">
                  <label className="form-label">تاریخ برداشت</label>
                  <DatePicker
                    value={formData.withdrawal_date}
                    onChange={(date) => {
                      if (date) {
                        setFormData({ ...formData, withdrawal_date: date });
                      }
                    }}
                    calendar={dateConfig.calendar}
                    locale={dateConfig.locale}
                    calendarPosition="bottom-right"
                    inputClass="form-control"
                  />
                </div>

                <div className="col-12">
                  <label className="form-label">نرخ ارز تبدیل</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      value={formData.exchange_rate}
                      disabled={formData.currency !== 'IRR'}
                      onChange={(e) => {
                        const rawValue = parseFormattedNumber(e.target.value);
                        if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
                          setFormData({ ...formData, exchange_rate: formatNumber(rawValue) });
                        }
                      }}
                      placeholder="مثلاً 55000"
                    />
                    <span className="input-group-text">
                      ریال
                    </span>
                  </div>
                  {formData.currency !== 'IRR' && (
                    <small className="text-muted d-block mt-1">فقط برای برداشت ریالی استفاده می‌شود</small>
                  )}
                </div>
                <div className="col-12">
                  <label className="form-label">توضیحات کوتاه</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="مانند علت برداشت"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">جزئیات</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={formData.details}
                    onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                    placeholder="توضیح کامل نحوه انتقال یا استفاده"
                  />
                </div>
                <div className="col-12">
                  <button type="submit" className="btn btn-primary w-100">
                    ثبت برداشت
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-8">
          <div className="d-grid gap-3">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-transparent border-bottom-0 pb-0">
                <h2 className="h6 mb-1">فیلتر لیست برداشت‌ها</h2>
                <p className="text-muted small mb-0">مدیر، ارز و بازه زمانی را انتخاب کنید</p>
              </div>
              <div className="card-body">
                <div className="row g-3 align-items-end">
                  <div className="col-12 col-md-4">
                    <label className="form-label">مدیر</label>
                    <select
                      className="form-select"
                      value={filters.manager_id}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          manager_id: e.target.value,
                        }))
                      }
                    >
                      <option value="">همه مدیران</option>
                      {managers.map((manager) => (
                        <option key={manager.id} value={manager.id}>
                          {manager.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-3">
                    <label className="form-label">نوع ارز</label>
                    <select
                      className="form-select"
                      value={filters.currency}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          currency: e.target.value,
                        }))
                      }
                    >
                      <option value="">همه</option>
                      <option value="IRR">ریال</option>
                      <option value="USD">دلار</option>
                    </select>
                  </div>
                  <div className="col-12 col-md-3">
                    <label className="form-label">از تاریخ</label>
                    <DatePicker
                      value={filters.startDate}
                      onChange={(date) =>
                        setFilters((prev) => ({
                          ...prev,
                          startDate: date || null,
                        }))
                      }
                      calendar={dateConfig.calendar}
                      locale={dateConfig.locale}
                      calendarPosition="bottom-right"
                      inputClass="form-control"
                    />
                  </div>
                  <div className="col-12 col-md-3">
                    <label className="form-label">تا تاریخ</label>
                    <DatePicker
                      value={filters.endDate}
                      onChange={(date) =>
                        setFilters((prev) => ({
                          ...prev,
                          endDate: date || null,
                        }))
                      }
                      calendar={dateConfig.calendar}
                      locale={dateConfig.locale}
                      calendarPosition="bottom-right"
                      inputClass="form-control"
                    />
                  </div>
                  <div className="col-12 col-md-2">
                    <button type="button" className="btn btn-outline-secondary w-100" onClick={resetFilters}>
                      بازنشانی
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-transparent border-bottom-0 pb-0 d-flex justify-content-between align-items-center">
                <div>
                  <h2 className="h5 mb-1">لیست برداشت‌ها</h2>
                  <p className="text-muted small mb-0">پیگیری برداشت‌های ثبت‌شده</p>
                </div>
                <span className="badge bg-primary text-white px-3 py-2">{withdrawals.length} رکورد</span>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th scope="col">ردیف</th>
                        <th scope="col">مدیر</th>
                        <th scope="col">نوع ارز</th>
                        <th scope="col">مبلغ</th>
                        <th scope="col">نرخ ارز  </th>
                        <th scope="col">معادل دلار</th>
                        <th scope="col">تاریخ</th>
                        <th scope="col">توضیحات</th>
                        <th scope="col">جزئیات</th>
                        <th scope="col" className="text-end">عملیات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {withdrawalsPagination.totalItems === 0 && (
                        <tr>
                          <td colSpan="8" className="text-center text-muted py-4">
                            برداشتی ثبت نشده است
                          </td>
                        </tr>
                      )}
                      {withdrawalsPagination.paginatedItems.map((withdrawal, index) => {
                        const hasAmount = withdrawal.amount !== null && withdrawal.amount !== undefined;
                        const amountValue = hasAmount ? parseFloat(withdrawal.amount) : null;
                        const exchangeRateValue =
                          withdrawal.exchange_rate !== null && withdrawal.exchange_rate !== undefined
                            ? parseFloat(withdrawal.exchange_rate)
                            : null;
                        const isValidAmount = amountValue !== null && !Number.isNaN(amountValue);
                        const isValidRate =
                          exchangeRateValue !== null && !Number.isNaN(exchangeRateValue) && exchangeRateValue > 0;
                        const usdEquivalentRaw =
                          withdrawal.currency === 'IRR' && isValidAmount && isValidRate
                            ? amountValue / exchangeRateValue
                            : withdrawal.currency === 'USD' && isValidAmount
                            ? amountValue
                            : null;
                        const usdEquivalent =
                          usdEquivalentRaw !== null && isFinite(usdEquivalentRaw)
                            ? parseFloat(usdEquivalentRaw.toFixed(2))
                            : null;

                        return (
                          <tr key={withdrawal.id}>
                            <td>
                              <span className="badge bg-secondary">{withdrawalsPagination.rangeStart + index}</span>
                            </td>
                            <td>{withdrawal.manager_name}</td>
                            <td>{withdrawal.currency === 'USD' ? 'دلار' : 'تومان'}</td>
                            <td>
                              <div className="fw-bold text-dark">
                                {formatNumber(isValidAmount ? amountValue : withdrawal.amount)}
                              </div>
                           
                              
                             
                            </td>
                            <td>{formatNumber(exchangeRateValue)}  </td>
                            <td>{formatNumber(usdEquivalent)}  </td>
                            <td>{formatDateForDisplay(withdrawal.withdrawal_date, dateLocale)}</td>
                            <td>{withdrawal.description || <span className="text-muted">بدون توضیح</span>}</td>
                            <td>{withdrawal.details || <span className="text-muted">اطلاعی ثبت نشده</span>}</td>
                            <td className="text-end">
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDelete(withdrawal.id)}
                              >
                                حذف
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {withdrawalsPagination.totalItems > 0 && (
                      <tfoot className="table-light">
                        <tr>
                          <td colSpan="3" />
                          <td className="fw-bold text-primary">
                            {formatNumber(totalAmount)}
                          </td>
                          <td></td>
                          <td className="fw-bold text-primary">{formatNumber(totalUsdEquivalent)}  </td>
                          <td colSpan="4" />
                        </tr>
                        
                      </tfoot>
                    )}
                  </table>
                </div>
                {renderPaginationControls(withdrawalsPagination)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Withdrawals;

